/**
 * contextTreeApi.ts
 *
 * Typed API client functions for tree-mode context execution and children loading.
 */

import type { ApiClient } from '@center-technologies/shared-api-clients';
import type {
  ContextDefinition,
  ContextTreeExecutionResult,
  ChildrenResult,
  EnrichmentOverrides,
  RuntimeFilterEnvelope,
} from 'types/context';
import type { UnifiedFilters } from 'types/filters';

/** Parameters for executing a context in tree mode */
export interface ExecuteContextTreeParams {
  rootSphereId: string;
  limit?: number;
  depth?: number;
  skipCache?: boolean;
  refreshStructure?: boolean;
  lazyLoad?: boolean;
  enrichmentOverrides?: EnrichmentOverrides;
  /**
   * A bare `UnifiedFilters` is applied to every level (today's behaviour,
   * `AtlasTree`'s debounced filters); a `RuntimeFilterEnvelope` additionally
   * carries `byLevelId` (EAINT-781 `useLevelFilters`). Spread verbatim into
   * the POST body either way — the route reads the same union.
   */
  runtimeFilters?: RuntimeFilterEnvelope | UnifiedFilters;
  /** View-declared sphere property names to project (date/text columns). */
  columnProperties?: string[];
}

/** Parameters for loading children of a specific node */
export interface LoadContextChildrenParams {
  parentSphereId: string;
  level: number;
  rootSphereId: string;
  limit?: number;
  token?: string;
  skipCache?: boolean;
  enrichmentOverrides?: EnrichmentOverrides;
  /** See `ExecuteContextTreeParams.runtimeFilters`. */
  runtimeFilters?: RuntimeFilterEnvelope | UnifiedFilters;
  /** View-declared sphere property names to project (date/text columns). */
  columnProperties?: string[];
  /** Ephemeral per-node traversal direction override (UI direction pill). */
  direction?: 'inwards' | 'outwards' | 'both';
}

/**
 * Execute a context query in tree mode against graph-service.
 *
 * @param graphClient - Graph service API client
 * @param systemName - Context system name
 * @param params - Execution parameters
 */
export async function executeContextTree(
  graphClient: ApiClient,
  systemName: string,
  params: ExecuteContextTreeParams
): Promise<ContextTreeExecutionResult> {
  const { rootSphereId, limit, depth, skipCache, refreshStructure, lazyLoad, enrichmentOverrides, runtimeFilters, columnProperties } = params;
  const hasColumnProperties = Boolean(columnProperties?.length);

  // Use POST when runtime filters are present to avoid URL length limits
  if (runtimeFilters) {
    const url = `/contexts/${encodeURIComponent(systemName)}/execute`;
    return graphClient.post<ContextTreeExecutionResult>(url, {
      rootSphereId,
      responseMode: 'tree',
      ...(limit !== undefined && { limit }),
      ...(depth !== undefined && { depth }),
      ...(skipCache && { skipCache: true }),
      ...(refreshStructure && { refreshStructure: true }),
      ...(enrichmentOverrides?.includeStatus !== undefined && { includeStatus: enrichmentOverrides.includeStatus }),
      ...(enrichmentOverrides?.includeFlow !== undefined && { includeFlow: enrichmentOverrides.includeFlow }),
      ...(enrichmentOverrides?.includeEdgeCount !== undefined && { includeEdgeCount: enrichmentOverrides.includeEdgeCount }),
      ...(enrichmentOverrides?.includePerspectives !== undefined && { includePerspectives: enrichmentOverrides.includePerspectives }),
      ...(enrichmentOverrides?.includeApproachDeployments !== undefined && { includeApproachDeployments: enrichmentOverrides.includeApproachDeployments }),
      ...(enrichmentOverrides?.includePoints !== undefined && { includePoints: enrichmentOverrides.includePoints }),
      ...(hasColumnProperties && { columnProperties }),
      runtimeFilters,
    });
  }

  const queryParams = new URLSearchParams();
  queryParams.set('rootSphereId', rootSphereId);
  queryParams.set('responseMode', 'tree');
  if (limit !== undefined) queryParams.set('limit', String(limit));
  if (depth !== undefined) queryParams.set('depth', String(depth));
  if (skipCache) queryParams.set('skipCache', 'true');
  if (refreshStructure) queryParams.set('refreshStructure', 'true');
  if (lazyLoad !== undefined) queryParams.set('lazyLoad', String(lazyLoad));
  if (enrichmentOverrides?.includeStatus !== undefined) queryParams.set('includeStatus', String(enrichmentOverrides.includeStatus));
  if (enrichmentOverrides?.includeFlow !== undefined) queryParams.set('includeFlow', String(enrichmentOverrides.includeFlow));
  if (enrichmentOverrides?.includeEdgeCount !== undefined) queryParams.set('includeEdgeCount', String(enrichmentOverrides.includeEdgeCount));
  if (enrichmentOverrides?.includePerspectives !== undefined) queryParams.set('includePerspectives', String(enrichmentOverrides.includePerspectives));
  if (enrichmentOverrides?.includeApproachDeployments !== undefined) queryParams.set('includeApproachDeployments', String(enrichmentOverrides.includeApproachDeployments));
  if (enrichmentOverrides?.includePoints !== undefined) queryParams.set('includePoints', String(enrichmentOverrides.includePoints));
  // Repeated key so the backend's array-typed querystring schema parses a list.
  if (columnProperties?.length) {
    for (const prop of columnProperties) queryParams.append('columnProperties', prop);
  }

  const url = `/contexts/${encodeURIComponent(systemName)}/execute?${queryParams.toString()}`;
  return graphClient.get<ContextTreeExecutionResult>(url);
}

/**
 * Load children for a specific parent node in tree mode.
 *
 * @param graphClient - Graph service API client
 * @param systemName - Context system name
 * @param params - Children loading parameters
 */
export async function loadContextChildren(
  graphClient: ApiClient,
  systemName: string,
  params: LoadContextChildrenParams
): Promise<ChildrenResult> {
  const { parentSphereId, level, rootSphereId, limit, token, skipCache, enrichmentOverrides, runtimeFilters, columnProperties, direction } = params;
  const hasColumnProperties = Boolean(columnProperties?.length);

  // Use POST when runtime filters are present to avoid URL length limits
  if (runtimeFilters) {
    const url = `/contexts/${encodeURIComponent(systemName)}/execute/children`;
    return graphClient.post<ChildrenResult>(url, {
      parentSphereId,
      level,
      rootSphereId,
      ...(limit !== undefined && { limit }),
      ...(token && { token }),
      ...(skipCache && { skipCache: true }),
      ...(enrichmentOverrides?.includeStatus !== undefined && { includeStatus: enrichmentOverrides.includeStatus }),
      ...(enrichmentOverrides?.includeFlow !== undefined && { includeFlow: enrichmentOverrides.includeFlow }),
      ...(enrichmentOverrides?.includeEdgeCount !== undefined && { includeEdgeCount: enrichmentOverrides.includeEdgeCount }),
      ...(enrichmentOverrides?.includePerspectives !== undefined && { includePerspectives: enrichmentOverrides.includePerspectives }),
      ...(enrichmentOverrides?.includeApproachDeployments !== undefined && { includeApproachDeployments: enrichmentOverrides.includeApproachDeployments }),
      ...(enrichmentOverrides?.includePoints !== undefined && { includePoints: enrichmentOverrides.includePoints }),
      ...(hasColumnProperties && { columnProperties }),
      ...(direction && { direction }),
      runtimeFilters,
    });
  }

  const queryParams = new URLSearchParams();
  queryParams.set('parentSphereId', parentSphereId);
  queryParams.set('level', String(level));
  queryParams.set('rootSphereId', rootSphereId);
  if (limit !== undefined) queryParams.set('limit', String(limit));
  if (token) queryParams.set('token', token);
  if (skipCache) queryParams.set('skipCache', 'true');
  if (enrichmentOverrides?.includeStatus !== undefined) queryParams.set('includeStatus', String(enrichmentOverrides.includeStatus));
  if (enrichmentOverrides?.includeFlow !== undefined) queryParams.set('includeFlow', String(enrichmentOverrides.includeFlow));
  if (enrichmentOverrides?.includeEdgeCount !== undefined) queryParams.set('includeEdgeCount', String(enrichmentOverrides.includeEdgeCount));
  if (enrichmentOverrides?.includePerspectives !== undefined) queryParams.set('includePerspectives', String(enrichmentOverrides.includePerspectives));
  if (enrichmentOverrides?.includeApproachDeployments !== undefined) queryParams.set('includeApproachDeployments', String(enrichmentOverrides.includeApproachDeployments));
  if (enrichmentOverrides?.includePoints !== undefined) queryParams.set('includePoints', String(enrichmentOverrides.includePoints));
  // Repeated key so the backend's array-typed querystring schema parses a list.
  if (columnProperties?.length) {
    for (const prop of columnProperties) queryParams.append('columnProperties', prop);
  }
  if (direction) queryParams.set('direction', direction);

  const url = `/contexts/${encodeURIComponent(systemName)}/execute/children?${queryParams.toString()}`;
  return graphClient.get<ChildrenResult>(url);
}

/** Parameters for executing an inline context definition (no save required) */
export interface ExecuteDefinitionParams {
  definition: ContextDefinition;
  rootSphereId: string;
  responseMode?: 'columns' | 'tree';
  limit?: number;
  depth?: number;
  enrichmentOverrides?: EnrichmentOverrides;
}

/**
 * Execute an inline context definition against graph-service.
 * Uses POST to send the definition in the request body (no DB lookup).
 *
 * @param graphClient - Graph service API client
 * @param params - Definition and execution parameters
 */
export async function executeContextDefinition(
  graphClient: ApiClient,
  params: ExecuteDefinitionParams
): Promise<ContextTreeExecutionResult> {
  const { definition, rootSphereId, responseMode, limit, depth, enrichmentOverrides } = params;
  return graphClient.post<ContextTreeExecutionResult>('/contexts/execute-definition', {
    definition,
    rootSphereId,
    ...(responseMode !== undefined && { responseMode }),
    ...(limit !== undefined && { limit }),
    ...(depth !== undefined && { depth }),
    ...(enrichmentOverrides?.includeStatus !== undefined && { includeStatus: enrichmentOverrides.includeStatus }),
    ...(enrichmentOverrides?.includeFlow !== undefined && { includeFlow: enrichmentOverrides.includeFlow }),
    ...(enrichmentOverrides?.includeEdgeCount !== undefined && { includeEdgeCount: enrichmentOverrides.includeEdgeCount }),
    ...(enrichmentOverrides?.includePerspectives !== undefined && { includePerspectives: enrichmentOverrides.includePerspectives }),
    ...(enrichmentOverrides?.includeApproachDeployments !== undefined && { includeApproachDeployments: enrichmentOverrides.includeApproachDeployments }),
    ...(enrichmentOverrides?.includePoints !== undefined && { includePoints: enrichmentOverrides.includePoints }),
  });
}
